import { useEffect, useState } from "react"

import AppStoreIcon from "@fuse/assets/icons/icon-app-store.svg"
import GooglePlayIcon from "@fuse/assets/icons/icon-google-play.svg"
import InstagramIcon from "@fuse/assets/icons/icon-instagram.svg"
import XIcon from "@fuse/assets/icons/icon-x.svg"

export const deepLinkURL = "letshang.co/"

export const playStoreLink =
  "https://play.google.com/store/apps/details?id=com.walkme.walkmeapp"
export const appStoreLink =
  "https://apps.apple.com/us/app/lets-hang/id1547911833"

export const socialLinksForFooter = [
  {
    name: "playStore",
    icon: GooglePlayIcon,
    linkUrl: playStoreLink,
  },
  {
    name: "appStore",
    icon: AppStoreIcon,
    linkUrl: appStoreLink,
  },
  {
    name: "X",
    icon: XIcon,
    linkUrl: "https://x.com/lets_hang_app_",
  },
  {
    name: "instagram",
    icon: InstagramIcon,
    linkUrl: "https://www.instagram.com/letshang.app",
  },
]

export enum DeviceTypes {
  NON_MOBILE = "non_mobile",
  IOS = "ios",
  ANDROID = "android",
}

export enum DeepLinkPath {
  Events = "events",
  Groups = "groups",
  Activity = "activity",
  VerifyEmail = "verify-email",
  Ticket = "Ticket",
  ClaimTicket = "claim_ticket",
}

interface generateDeepLinkType {
  path: DeepLinkPath
  id: string
}

interface shareProps {
  message: string
  metadata?: object
}

export const DownloadLinkMap = {
  [DeviceTypes.ANDROID]:
    "https://play.google.com/store/apps/details?id=com.walkme.walkmeapp",
  [DeviceTypes.IOS]: "https://apps.apple.com/us/app/lets-hang/id1547911833",
  [DeviceTypes.NON_MOBILE]:
    "https://play.google.com/store/apps/details?id=com.walkme.walkmeapp",
}

export const onOpenDownloadLink = (link: string) => {
  window.location.href = link
  return
}

export const useDeviceDetection = () => {
  const [device, setDevice] = useState(DeviceTypes.NON_MOBILE)

  useEffect(() => {
    const handleDeviceDetection = () => {
      const userAgent = navigator.userAgent.toLowerCase()

      const isIOS = /iphone|ipad|macintosh|ipod/g.test(userAgent)
      const isAndroid =
        /(tablet|playbook|silk)|android|(android(?!.*mobile))|blackberry|windows phone/g.test(
          userAgent,
        )

      if (isIOS) {
        setDevice(DeviceTypes.IOS)
      } else if (isAndroid) {
        setDevice(DeviceTypes.ANDROID)
      } else {
        setDevice(DeviceTypes.NON_MOBILE)
      }
    }

    handleDeviceDetection()
    window.addEventListener("resize", handleDeviceDetection)

    return () => {
      window.removeEventListener("resize", handleDeviceDetection)
    }
  }, [])

  return device
}

export function isDev(): boolean {
  return import.meta.env.MODE === "development"
}

export function isStaging(): boolean {
  return import.meta.env.MODE === "staging"
}

export function isProd(): boolean {
  return import.meta.env.MODE === "production"
}

// 🔥 DEV-ONLY: Fake email verification utilities
export const devEmailVerificationUtils = {
  /**
   * Check if user has fake verification stored in localStorage
   */
  isDevVerified: (uid: string): boolean => {
    if (!isDev()) return false
    return localStorage.getItem(`dev_email_verified_${uid}`) === "true"
  },

  /**
   * Get the fake verified email from localStorage
   */
  getDevVerifiedEmail: (uid: string): string | null => {
    if (!isDev()) return null
    return localStorage.getItem(`dev_verified_email_${uid}`)
  },

  /**
   * Clear fake verification when user logs out
   */
  clearDevVerification: (uid: string): void => {
    if (!isDev()) return
    localStorage.removeItem(`dev_email_verified_${uid}`)
    localStorage.removeItem(`dev_verified_email_${uid}`)
    console.log("🗑️ DEV: Cleared fake email verification")
  },

  /**
   * Clear all dev verification data (useful for complete cleanup)
   */
  clearAllDevVerification: (): void => {
    if (!isDev()) return
    const keys = Object.keys(localStorage)
    keys.forEach((key) => {
      if (
        key.startsWith("dev_email_verified_") ||
        key.startsWith("dev_verified_email_")
      ) {
        localStorage.removeItem(key)
      }
    })
    console.log("🗑️ DEV: Cleared all fake email verification data")
  },
}

export const generateDeepLink = async ({
  path,
  id,
}: generateDeepLinkType): Promise<{ shortLink: string }> => {
  return new Promise((resolve, reject) => {
    resolve({ shortLink: `${deepLinkURL}share/${path}/${id}` })
  })
}

export const splitFullName = (fullName: string) => {
  // Split the full name by space
  if (!fullName) return { firstName: "", lastName: "" }
  const nameParts = fullName.trim().split(" ")

  // If the name consists of more than one word, return first and last names
  if (nameParts.length > 1) {
    const firstName = nameParts[0]
    const lastName = nameParts.slice(1).join(" ") // Join the rest as last name
    return { firstName, lastName }
  }

  // If there's only one name (e.g., just "John"), consider it the first name
  return { firstName: nameParts[0], lastName: "" }
}
